/**
 * A surface's stored theme -> the token values the spine reads.
 *
 * "A brand is a token file" is the project's central claim, and this is where a
 * Drop's `themeColors` actually becomes one. Without it the claim was only
 * asserted: `DropPageMain` published the brand colour as `--drop-accent`, a
 * name the contract does not contain and the ported spine never reads, so
 * Modern Warfare 4 rendered its gold campaign entirely in DevourPlay red — the
 * platform default, on a publisher's own page.
 *
 * ## Contrast is assigned by ROLE, not by value
 *
 * `--accent` paints FILLS: the CTA, the meters, the pulsing dot, the play
 * button. Any brand value is permitted there, because a fill has no legibility
 * requirement of its own.
 *
 * `--accent-ink` sets TYPE and must clear 4.5:1 on the ground it sits on. That
 * is why they are two tokens: Team Liquid navy is 1.5:1 against the page and
 * GCU purple is 2.0:1, so a single token used for both makes the type
 * invisible. When a brand's accent fails as ink, the ink falls back rather than
 * the fill changing — a legible page in the brand's colour beats an illegible
 * one in the exact colour.
 *
 * ## `--oi` is the other direction and cannot be defaulted
 *
 * It is the ink ON an accent fill: what "PRE-ORDER" is set in when the button
 * behind it is the brand's gold. Pure black works for a light accent and
 * disappears against a dark one, so it is chosen by the accent's own luminance
 * rather than fixed.
 */

import React from "react";
import {parseHex, relativeLuminance, contrastRatio, AA_NORMAL} from "../brandTokens";

export interface SurfaceThemeColors {
    primary?: string;
    secondary?: string;
    background?: string;
}

/**
 * Build the inline custom-property block for a surface.
 *
 * Returned as a style object rather than written to a stylesheet because the
 * values belong to ONE surface. A brand's colours in a global sheet are a
 * global change; on the element they scope themselves.
 */
export function brandStyle(theme?: SurfaceThemeColors): React.CSSProperties | undefined {
    const accent = theme?.primary;
    if (!accent || !parseHex(accent)) return undefined;

    const ground = theme?.background && parseHex(theme.background) ? theme.background : "#050505";

    /*
     * Ink must be readable. `contrastRatio` returns null on an unparseable
     * pair, which is treated as failing — an unknown ratio is not a passing
     * one, and defaulting the other way puts unreadable type on the page.
     */
    const ratio = contrastRatio(accent, ground);
    const inkReadable = ratio !== null && ratio >= AA_NORMAL;

    /*
     * Ink ON the accent. A light fill takes dark ink and a dark fill takes
     * light ink; the threshold is the accent's own luminance rather than a
     * lightness guess, so it is the same measurement the contrast check uses.
     */
    const rgb = parseHex(accent)!;
    const onAccent = relativeLuminance(rgb) > 0.35 ? "#0d0d0f" : "#ffffff";

    return {
        "--accent": accent,
        "--accent-2": accent,
        "--accent-glow": `${accent}4d`,
        /*
         * The fallback is the platform's own accent-2, NOT a greyed-out
         * version of the brand: a brand colour dimmed until it passes reads as
         * a rendering fault, while the platform accent reads as a deliberate
         * choice. The fill above still carries the brand.
         */
        "--accent-ink": inkReadable ? accent : "var(--accent-2)",
        "--oi": onAccent,
        /* The hub dialect's alias, published so ported rules resolve too. */
        "--o": inkReadable ? accent : "var(--accent-2)",
        "--o2": accent,
    } as React.CSSProperties;
}
